import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AddModal } from '../../../../component/modal';
import { colors } from '../../../../component/colors';
import { DropDownWithLabel } from '../../../../component/dropDown';
import { Input } from '../../../../component/input';
import { ImagePicker } from '../../../../component/imagePicker';
import { useFormik } from 'formik';
import { showErrorToast, showSuccessToast } from '../../../../utils/toast';
import { GooglePlace } from '../../../../component/googlePlace';
import { useFetchAgentQuery } from '../../../../store/agent/api';
import { addStoreValidation } from '../../validation';
import { useAddStoreMutation } from '../../../../store/shopping/api';
import { states } from '../../../../utils/nigeriaSates';
import { SearchableDropDownWithLabel } from '../../../../component/dropDown/searchableDropDownWthLabel';
import { generateUrl } from '../../../../utils/urlGenerator';
import useDebounce from '../../../../utils/useDebounce';
type IAddStore = {
  show?: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
};
type FormikValues = {
  name: string;
  type: string;
  agent: {
    name: string;
    index: number;
  };
  address: {
    address: string;
    lat: number;
    lng: number;
    city: string;
  };
  state: string;
  file: File[] | null;
};
export const AddStore: FC<IAddStore> = ({ setShow }) => {
  const [searchAgent, setSearchAgent] = useState('');
  const debounceSearchAgent = useDebounce(searchAgent, 500);
  const { data: agents } = useFetchAgentQuery(
    generateUrl({ module: 'Shopping', search: debounceSearchAgent })
  );
  const [addStore, { isLoading: isAddingStore }] = useAddStoreMutation();
  const handleFilePicked = (file: File[]) => {
    setFieldValue('file', file);
  };
  const agentsList = useMemo(() => {
    return (
      agents?.data.fetchedData.map((agents) => ({
        name: agents.name,
        imageUrl: agents.file.url
      })) ?? []
    );
  }, [agents]);

  const handleAddProperty = (values: FormikValues) => {
    if (!agents?.data) return;
    if (!values.file) {
      showErrorToast('image is required');
      return;
    }
    const formData = new FormData();
    formData.append('name', values.name.toLowerCase());
    formData.append('type', values.type.toLowerCase());
    formData.append('agent', agents?.data.fetchedData[values.agent.index]._id);
    formData.append('state', values.state);
    formData.append('address', values.address.address);
    formData.append('coordinates[longitude]', values.address.lng.toString());
    formData.append('coordinates[latitude]', values.address.lat.toString());

    values.file.forEach((file) => {
      formData.append('file', file);
    });
    addStore(formData)
      .unwrap()
      .then((result) => {
        showSuccessToast(result.message);
        setShow(false);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };
  const { values, touched, handleChange, handleSubmit, setFieldValue, errors } =
    useFormik({
      initialValues: {
        name: '',
        type: '',
        agent: { name: '', index: 0 },
        state: '',
        address: { address: '', lat: 0, lng: 0, city: '' },
        file: null
      },
      validationSchema: addStoreValidation,
      onSubmit: handleAddProperty
    });

  return (
    <AddModal
      title="Add Store"
      disableBtn={isAddingStore}
      btnTxt={isAddingStore ? 'Loading...' : 'Add Store'}
      btnClick={handleSubmit}
      closeModal={() => setShow(false)}
      isLoading={isAddingStore}
    >
      <Container>
        <Row>
          <CustomInput
            labelText="Name"
            value={values.name}
            onChange={handleChange('name')}
            placeholder="Enter Name"
            errorMessage={touched.name ? errors.name : ''}
          />

          <CustomDropDown
            label="Type"
            value={values.type}
            errorMessage={touched.type ? errors.type : ''}
            onSelect={(item) => {
              setFieldValue('type', item);
            }}
            options={['Mall', 'Supermarket', 'Store']}
          />
        </Row>
        <Row>
          <CustomGooglePlace
            onSelect={(value) => setFieldValue('address', value)}
            placeholder="Enter address"
            label="Address"
            errorMessage={touched.address && errors.address ? '*required' : ''}
          />
          <CustomDropDown
            label="State"
            value={values.state}
            errorMessage={touched.state ? errors.state : ''}
            onSelect={(item) => {
              setFieldValue('state', item);
            }}
            options={states}
          />
        </Row>
        <Row>
          <SearchableCustomDropDown
            search={searchAgent}
            setSearch={setSearchAgent}
            label="SOJI agent"
            value={values.agent.name}
            onSelect={(item, index) => {
              setFieldValue('agent', { name: item, index });
            }}
            options={agentsList}
            errorMessage={touched.agent && errors.agent ? '*required' : ''}
          />
        </Row>
        <CustomImagePicker onFilePicked={handleFilePicked} />
      </Container>
    </AddModal>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0px 40px;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`;
const CustomGooglePlace = styled(GooglePlace)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomInput = styled(Input)`
  flex: 0.46;
  color: ${colors.textDark};
  margin-bottom: 16px;
`;
const CustomImagePicker = styled(ImagePicker)`
  margin-bottom: 32px;
`;

const CustomDropDown = styled(DropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const SearchableCustomDropDown = styled(SearchableDropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
